import { Component, OnInit, inject, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CrmService,
  CasoDto,
  EmbudoDto,
  FichaContacto,
  CitaClinica,
  ActividadDto,
} from '../core/crm.service';

type RedSocialFiltro = 'todos' | 'whatsapp' | 'instagram' | 'messenger' | 'tiktok' | 'youtube' | 'google' | 'pbx';
type TipoInteraccionFiltro = 'todos' | 'dm' | 'comentario';

interface ContextoRedSocial {
  redClave: Exclude<RedSocialFiltro, 'todos'>;
  redNombre: string;
  sigla: string;
  esComentarioPublico: boolean;
  tipoEtiqueta: string;
  publicacionRef: string;
  comentarioOriginal: string;
  usuarioRed: string;
}

@Component({
  selector: 'app-conversaciones',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="omni-page">
      <!-- BARRA SUPERIOR OMNICANAL: TODAS LAS REDES SOCIALES + FILTRO DM VS COMENTARIOS -->
      <section class="omni-top-bar card">
        <div class="omni-top-row">
          <div>
            <div class="omni-title">Chats y Redes Sociales · Bandeja Omnicanal</div>
            <div class="omni-sub">
              Mensajes directos (DM), comentarios en Reels/Posts/Videos y reseñas públicas en tiempo real
            </div>
          </div>

          <div class="omni-type-switch">
            <button
              type="button"
              class="omni-type-btn"
              [class.on]="filtroTipo() === 'todos'"
              (click)="filtroTipo.set('todos')"
            >
              Todos ({{ casos().length }})
            </button>
            <button
              type="button"
              class="omni-type-btn"
              [class.on]="filtroTipo() === 'dm'"
              (click)="filtroTipo.set('dm')"
            >
              💬 Mensajes Directos ({{ contarPorTipo('dm') }})
            </button>
            <button
              type="button"
              class="omni-type-btn"
              [class.on]="filtroTipo() === 'comentario'"
              (click)="filtroTipo.set('comentario')"
            >
              📢 Comentarios y Reseñas ({{ contarPorTipo('comentario') }})
            </button>
          </div>
        </div>

        <div class="omni-networks-row">
          @for (red of redesDisponibles; track red.id) {
            <button
              type="button"
              class="omni-net-pill"
              [attr.data-net]="red.id"
              [class.selected]="filtroRed() === red.id"
              (click)="filtroRed.set(red.id)"
            >
              <span class="net-dot" [attr.data-net]="red.id"></span>
              <span>{{ red.nombre }}</span>
              <span class="net-count">({{ contarPorRed(red.id) }})</span>
            </button>
          }
        </div>
      </section>

      <div class="inbox-workspace">
        <!-- Columna izquierda: Bandeja de casos y comentarios de todas las redes -->
        <aside class="inbox-sidebar card">
          <div class="inbox-side-hd">
            <div style="font-size: 13.5px; font-weight: 800; color: var(--ink);">
              Bandeja Activa ({{ casosFiltrados().length }})
            </div>
            <div class="m">WhatsApp · IG · FB · TikTok · YouTube · Google</div>
          </div>

          <div class="inbox-items">
            @for (c of casosFiltrados(); track c.id) {
              @let ctx = obtenerContextoRed(c);
              <div
                class="inbox-item"
                [class.active]="seleccionado()?.id === c.id"
                (click)="seleccionarCaso(c)"
              >
                <div class="inbox-avatar" [attr.data-net]="ctx.redClave">{{ iniciales(c.contacto) }}</div>
                <div style="flex: 1; min-width: 0;">
                  <div style="display: flex; justify-content: space-between; align-items: center; gap: 4px;">
                    <b style="font-size: 12.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                      {{ c.contacto }}
                    </b>
                    <span class="inbox-ch" [attr.data-ch]="ctx.redClave">{{ ctx.sigla }}</span>
                  </div>

                  <div class="inbox-kind-row">
                    <span class="kind-chip" [class.is-comment]="ctx.esComentarioPublico">
                      {{ ctx.esComentarioPublico ? '📢 ' + ctx.tipoEtiqueta : '💬 Chat DM' }}
                    </span>
                    <span class="m" style="font-size: 10px;">{{ ctx.usuarioRed }}</span>
                  </div>

                  <div class="inbox-preview">{{ c.titulo }}</div>
                  <div class="m" style="margin-top: 2px;">{{ c.embudo }} · {{ c.etapa }}</div>
                </div>
              </div>
            } @empty {
              <div style="padding: 24px 14px; text-align: center;" class="m">
                No hay conversaciones o comentarios en esta red social con el filtro actual.
              </div>
            }
          </div>
        </aside>

        <!-- Columna central: Lienzo de conversación, lectura de comentario de red social y respuesta -->
        @if (seleccionado(); as sel) {
          @let ctxSel = obtenerContextoRed(sel);
          <section class="inbox-chat">
            <div class="card chat-header">
              <div style="display: flex; align-items: center; gap: 11px;">
                <div class="inbox-avatar" [attr.data-net]="ctxSel.redClave">{{ iniciales(sel.contacto) }}</div>
                <div>
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <span style="font-size: 14px; font-weight: 800; color: var(--ink);">{{ sel.contacto }}</span>
                    <span class="inbox-ch" [attr.data-ch]="ctxSel.redClave">{{ ctxSel.redNombre }}</span>
                    <span class="kind-chip" [class.is-comment]="ctxSel.esComentarioPublico">
                      {{ ctxSel.esComentarioPublico ? '📢 ' + ctxSel.tipoEtiqueta : '💬 Mensaje Directo' }}
                    </span>
                  </div>
                  <div class="m">{{ ctxSel.usuarioRed }} · {{ sel.embudo }}</div>
                </div>
              </div>

              <div style="display: flex; align-items: center; gap: 8px;">
                <button type="button" class="btn" style="height: 32px; font-size: 11.5px;" (click)="irAFicha(sel.contactoId)">
                  Expediente 360° →
                </button>
              </div>
            </div>

            <!-- BANNER DE CONTEXTO CUANDO ES UN COMENTARIO EN POST / REEL / VIDEO / RESEÑA -->
            @if (ctxSel.esComentarioPublico) {
              <div class="social-comment-banner" [attr.data-net]="ctxSel.redClave">
                <div class="scb-left">
                  <div class="scb-eyebrow">
                    📢 {{ ctxSel.tipoEtiqueta | uppercase }} EN {{ ctxSel.redNombre | uppercase }}
                  </div>
                  <div class="scb-post-title">{{ ctxSel.publicacionRef }}</div>
                  <div class="scb-comment-quote">"{{ ctxSel.comentarioOriginal }}"</div>
                </div>
                <div class="scb-actions">
                  <button
                    type="button"
                    class="scb-btn pub"
                    (click)="responderComentarioPublicoYDm(sel, false)"
                  >
                    📢 Responder en publicación
                  </button>
                  <button
                    type="button"
                    class="scb-btn dm"
                    (click)="responderComentarioPublicoYDm(sel, true)"
                  >
                    📩 Responder comentario + Enviar DM privado
                  </button>
                </div>
              </div>
            }

            <div class="chat-stream">
              <div class="msg-in">
                <div class="msg-meta">
                  {{ sel.contacto }} ({{ ctxSel.usuarioRed }}) · {{ ctxSel.redNombre }} ·
                  {{ ctxSel.esComentarioPublico ? ctxSel.tipoEtiqueta : 'Mensaje entrante' }}
                </div>
                <div>{{ ctxSel.comentarioOriginal }}</div>
                <div style="margin-top: 6px; font-size: 11.5px; color: #475569;">
                  Procedimiento / Interés detectado: <b>{{ sel.titulo }}</b>
                </div>
              </div>

              @for (act of actividadesOrdenadas(); track act.id) {
                @if (act.tipo === 'nota') {
                  <div class="msg-out">
                    <div class="msg-meta-out">C.O. Ebenezer · {{ act.creadoEn | date:'h:mm a' }}</div>
                    <div>{{ leerTextoNota(act) }}</div>
                  </div>
                } @else {
                  <div class="msg-event">
                    <span>{{ describirMovimiento(act) }} · {{ act.creadoEn | date:'dd MMM, h:mm a' }}</span>
                  </div>
                }
              }
            </div>

            <!-- Plantillas rápidas clínicas y de redes sociales de Ebenezer -->
            <div class="quick-templates">
              <span class="m" style="font-weight: 700;">Respuesta en 1 clic:</span>
              @if (ctxSel.esComentarioPublico) {
                <button
                  type="button"
                  class="tpl-chip"
                  (click)="usarPlantilla('¡Hola ' + sel.contacto + '! Gracias por escribirnos al Centro Oftalmológico Ebenezer 👀✨. Te acabamos de enviar la información de ' + sel.titulo + ' a tu bandeja de mensajes privados (DM) para agendar tu valoración.')"
                >
                  📢 Comentario + Paso a DM
                </button>
              }
              <button
                type="button"
                class="tpl-chip"
                (click)="usarPlantilla('Hola ' + sel.contacto + ', el primer paso para ' + sel.titulo + ' es agendar tu valoración oftalmológica en Ebenezer. ¿Te confirmo cupo en la mañana o en la tarde?')"
              >
                📅 Agendar valoración
              </button>
              <button
                type="button"
                class="tpl-chip"
                (click)="usarPlantilla('Hola ' + sel.contacto + ', recibimos tu orden médica para ' + sel.titulo + '. Procedemos a validar autorización y enviarte indicaciones prequirúrgicas.')"
              >
                ✅ Confirmar autorización
              </button>
              <button
                type="button"
                class="tpl-chip"
                (click)="usarPlantilla('📋 Indicaciones prequirúrgicas C.O. Ebenezer: asistir en ayuno de 6 horas, con acompañante mayor de edad y traer exámenes biométricos OD/OI.')"
              >
                🏥 Preparación pre-Qx
              </button>
              <button type="button" class="tpl-chip" (click)="adjuntarOrdenClinica(sel)">
                📄 Enviar Cotización PDF
              </button>
            </div>

            <form class="card chat-composer" (ngSubmit)="enviarNota()">
              <select
                class="channel-reply-select"
                name="canalRespuesta"
                [(ngModel)]="canalRespuesta"
                title="Seleccionar por cuál red o modalidad responder"
              >
                <option value="origen">Responder por {{ ctxSel.redNombre }}</option>
                <option value="comentario_dm">📢 Comentario Público + 📩 DM Privado</option>
                <option value="whatsapp">💬 Enviar por WhatsApp Business</option>
                <option value="instagram">📸 Enviar por Instagram DM</option>
                <option value="messenger">📘 Enviar por Facebook Messenger</option>
                <option value="tiktok">🎵 Enviar por TikTok</option>
                <option value="youtube">▶️ Responder en YouTube</option>
                <option value="google">⭐ Responder Reseña Google</option>
              </select>

              <button
                type="button"
                class="tpl-chip"
                title="Adjuntar archivo PDF o imagen clínica"
                (click)="inputAdjuntoChat.click()"
              >
                📎 Adjuntar
              </button>
              <input
                #inputAdjuntoChat
                type="file"
                accept=".pdf,.jpg,.png,.doc,.docx"
                style="display: none;"
                (change)="subirArchivoChat($event, sel)"
              />
              <input
                type="text"
                name="mensaje"
                placeholder="Escribe tu respuesta al comentario o mensaje directo del paciente..."
                [(ngModel)]="textoMensaje"
                [disabled]="enviando()"
              />
              <button type="submit" class="btn pri" style="height: 36px;" [disabled]="enviando() || !textoMensaje.trim()">
                Responder ↵
              </button>
            </form>
          </section>

          <!-- Columna derecha: Recorrido vertical conectado y Citas Clínicas -->
          <aside class="card inbox-right">
            <div style="font-size: 13px; font-weight: 800; color: var(--ink);">Recorrido del Caso</div>
            <div class="m" style="margin-bottom: 12px;">{{ sel.embudo }} (clic para avanzar)</div>

            <div class="v-stepper">
              @for (et of etapasCaso(sel); track et.id) {
                <div
                  class="v-step"
                  [class.done]="et.orden < ordenActual(sel)"
                  [class.current]="et.id === sel.etapaId"
                  (click)="cambiarEtapa(sel, et.id)"
                >
                  <div class="v-node">
                    @if (et.orden < ordenActual(sel)) { ✓ } @else { {{ et.orden }} }
                  </div>
                  <div style="flex: 1;">
                    <div class="v-name">{{ et.nombre }}</div>
                    <div class="m">
                      @if (et.orden < ordenActual(sel)) { Completada }
                      @else if (et.id === sel.etapaId) { En curso ahora }
                      @else { Pendiente }
                    </div>
                  </div>
                </div>
              }
            </div>

            <div style="margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--line);">
              <div style="font-size: 11px; font-weight: 800; color: var(--blue); margin-bottom: 8px;">
                CITAS CLÍNICAS DEL PACIENTE ({{ citas().length }})
              </div>
              @for (c of citas(); track c.fechaHora) {
                <div style="font-size: 11.5px; padding: 6px 0; border-bottom: 1px solid #F1F5F9;">
                  <b>{{ c.especialidad }}</b> · {{ c.estado | uppercase }}
                  <div class="m">{{ c.sede }} · {{ c.fechaHora | date:'dd MMM yyyy' }}</div>
                </div>
              } @empty {
                <div class="m">Sin citas médicas registradas.</div>
              }
            </div>

            <div style="margin-top: 12px; padding-top: 10px; border-top: 1px solid var(--line);">
              <div style="font-size: 11px; font-weight: 800; color: #64748B; margin-bottom: 6px;">
                REGISTRAR MOTIVO DE ESPERA / DESCARTE
              </div>
              <select
                style="width: 100%; height: 32px; border: 1px solid #CBD5E1; border-radius: 7px; font-size: 11.5px; padding: 0 8px;"
                (change)="registrarMotivoEspera(sel, $any($event.target).value)"
              >
                <option value="">Seleccionar motivo...</option>
                <option value="En espera de autorización EPS / Póliza">En espera de autorización EPS / Póliza</option>
                <option value="Evaluando cotización particular">Evaluando cotización particular</option>
                <option value="Se respondió comentario en red social y se envió DM">Se respondió comentario en red social y se envió DM</option>
                <option value="No contesta llamada / Se deja WhatsApp">No contesta llamada / Se deja WhatsApp</option>
                <option value="Pendiente exámenes prequirúrgicos">Pendiente exámenes prequirúrgicos</option>
              </select>
            </div>

            <button
              type="button"
              class="btn pri"
              style="width: 100%; margin-top: 14px;"
              (click)="cerrarCasoComoCompletado(sel)"
            >
              ✓ Marcar Caso Completado
            </button>
          </aside>
        }
      </div>
    </div>
  `,
  styles: [`
    .omni-page {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 14px 18px;
      height: calc(100vh - 50px);
      background: #EEF2F7;
      overflow: hidden;
    }

    .omni-top-bar {
      padding: 12px 16px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      flex-shrink: 0;
    }

    .omni-top-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
    }

    .omni-title {
      font-size: 15px;
      font-weight: 800;
      color: #0F172A;
    }

    .omni-sub {
      font-size: 11.5px;
      color: #64748B;
      margin-top: 2px;
    }

    .omni-type-switch {
      display: inline-flex;
      background: #F1F5F9;
      border: 1px solid #CBD5E1;
      border-radius: 8px;
      padding: 2px;
      gap: 3px;
    }

    .omni-type-btn {
      border: 0;
      background: transparent;
      color: #475569;
      font: 700 11px var(--font);
      padding: 5px 10px;
      border-radius: 6px;
      cursor: pointer;
    }
    .omni-type-btn.on {
      background: #0F172A;
      color: #FFFFFF;
    }

    .omni-networks-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 7px;
    }

    .omni-net-pill {
      border: 1px solid #CBD5E1;
      background: #FFFFFF;
      color: #1E293B;
      font: 700 11px var(--font);
      padding: 5px 11px;
      border-radius: 999px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      transition: all 0.14s;
    }
    .omni-net-pill:hover {
      border-color: #93C5FD;
      background: #F8FAFC;
    }
    .omni-net-pill.selected {
      background: #0F172A;
      border-color: #0F172A;
      color: #FFFFFF;
    }

    .net-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #186ADE;
    }
    .net-dot[data-net="whatsapp"] { background: #16A34A; }
    .net-dot[data-net="instagram"] { background: #E1306C; }
    .net-dot[data-net="messenger"] { background: #0084FF; }
    .net-dot[data-net="tiktok"] { background: #111827; }
    .net-dot[data-net="youtube"] { background: #DC2626; }
    .net-dot[data-net="google"] { background: #F59E0B; }
    .net-dot[data-net="pbx"] { background: #7C3AED; }

    .net-count {
      opacity: 0.8;
      font-size: 10.5px;
    }

    .inbox-workspace {
      flex: 1;
      display: flex;
      gap: 14px;
      min-height: 0;
    }

    .inbox-sidebar {
      width: 310px;
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .inbox-side-hd {
      padding: 12px 14px;
      border-bottom: 1px solid var(--line);
    }

    .inbox-items {
      flex: 1;
      overflow-y: auto;
      padding: 8px;
      display: flex;
      flex-direction: column;
      gap: 5px;
    }

    .inbox-item {
      display: flex;
      gap: 10px;
      padding: 10px 11px;
      border-radius: 10px;
      cursor: pointer;
      border: 1px solid transparent;
      transition: all .12s;
    }

    .inbox-item:hover {
      background: #F8FAFC;
    }

    .inbox-item.active {
      background: #EFF6FF;
      border-color: #BFDBFE;
    }

    .inbox-avatar {
      width: 34px;
      height: 34px;
      border-radius: 9px;
      background: linear-gradient(135deg, #0E2A4F 0%, #035CB1 100%);
      color: #FFFFFF;
      font-size: 11.5px;
      font-weight: 800;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .inbox-avatar[data-net="whatsapp"] { background: #16A34A; }
    .inbox-avatar[data-net="instagram"] { background: linear-gradient(135deg, #833AB4, #E1306C, #F77737); }
    .inbox-avatar[data-net="messenger"] { background: #0084FF; }
    .inbox-avatar[data-net="tiktok"] { background: #0F172A; }
    .inbox-avatar[data-net="youtube"] { background: #DC2626; }
    .inbox-avatar[data-net="google"] { background: #D97706; }

    .inbox-ch {
      font-size: 9.5px;
      font-weight: 800;
      padding: 2px 6px;
      border-radius: 5px;
      background: #E8F1FB;
      color: var(--blue);
      flex-shrink: 0;
    }
    .inbox-ch[data-ch="whatsapp"] { background: #DCFCE7; color: #15803D; }
    .inbox-ch[data-ch="instagram"] { background: #FCE7F3; color: #BE185D; }
    .inbox-ch[data-ch="messenger"] { background: #DBEAFE; color: #1D4ED8; }
    .inbox-ch[data-ch="tiktok"] { background: #E2E8F0; color: #0F172A; }
    .inbox-ch[data-ch="youtube"] { background: #FEE2E2; color: #B91C1C; }
    .inbox-ch[data-ch="google"] { background: #FEF3C7; color: #B45309; }
    .inbox-ch[data-ch="pbx"] { background: #EDE9FE; color: #6D28D9; }

    .inbox-kind-row {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 3px;
    }

    .kind-chip {
      font-size: 9.5px;
      font-weight: 700;
      padding: 1px 6px;
      border-radius: 4px;
      background: #F1F5F9;
      color: #334155;
    }
    .kind-chip.is-comment {
      background: #FEF3C7;
      color: #B45309;
      border: 1px solid #FDE68A;
    }

    .inbox-preview {
      font-size: 11.5px;
      color: var(--text);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-top: 3px;
    }

    .inbox-chat {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 9px;
      min-width: 0;
    }

    .chat-header {
      padding: 11px 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .social-comment-banner {
      background: #FFFFFF;
      border: 1px solid #DCE3EE;
      border-left: 4px solid #E1306C;
      border-radius: 10px;
      padding: 10px 14px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
    }
    .social-comment-banner[data-net="youtube"] { border-left-color: #DC2626; }
    .social-comment-banner[data-net="tiktok"] { border-left-color: #0F172A; }
    .social-comment-banner[data-net="messenger"] { border-left-color: #0084FF; }
    .social-comment-banner[data-net="google"] { border-left-color: #F59E0B; }

    .scb-eyebrow {
      font-size: 10px;
      font-weight: 800;
      color: #BE185D;
      letter-spacing: 0.03em;
    }
    .scb-post-title {
      font-size: 12.5px;
      font-weight: 800;
      color: #0F172A;
      margin-top: 2px;
    }
    .scb-comment-quote {
      font-size: 11.5px;
      color: #334155;
      font-style: italic;
      margin-top: 2px;
    }

    .scb-actions {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
    }

    .scb-btn {
      border-radius: 7px;
      font: 700 11px var(--font);
      padding: 6px 10px;
      cursor: pointer;
      border: 1px solid #CBD5E1;
      background: #F8FAFC;
      color: #0F172A;
    }
    .scb-btn.dm {
      background: #186ADE;
      border-color: #1459B8;
      color: #FFFFFF;
    }

    .chat-stream {
      flex: 1;
      overflow-y: auto;
      padding: 8px 4px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .msg-in {
      align-self: flex-start;
      max-width: 72%;
      background: #FFFFFF;
      border: 1px solid var(--line);
      padding: 11px 14px;
      border-radius: 12px 12px 12px 4px;
      box-shadow: var(--shadow-sm);
      font-size: 12.5px;
    }

    .msg-meta {
      font-size: 10.5px;
      font-weight: 700;
      color: var(--muted);
      margin-bottom: 4px;
    }

    .msg-out {
      align-self: flex-end;
      max-width: 72%;
      background: linear-gradient(135deg, #035CB1 0%, #0E2A4F 100%);
      color: #FFFFFF;
      padding: 11px 14px;
      border-radius: 12px 12px 4px 12px;
      box-shadow: 0 4px 12px rgba(3, 92, 177, .22);
      font-size: 12.5px;
    }

    .msg-meta-out {
      font-size: 10.5px;
      color: #BFDBFE;
      margin-bottom: 4px;
      font-weight: 600;
    }

    .msg-event {
      align-self: center;
      font-size: 11px;
      font-weight: 700;
      color: var(--blue);
      background: var(--blue-soft);
      padding: 4px 12px;
      border-radius: 999px;
    }

    .quick-templates {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
    }

    .tpl-chip {
      border: 1px solid #BBD4F2;
      background: #FFFFFF;
      color: var(--blue);
      font: 600 11px var(--font);
      padding: 4px 10px;
      border-radius: 999px;
      cursor: pointer;
    }
    .tpl-chip:hover {
      background: var(--blue);
      color: #FFFFFF;
    }

    .chat-composer {
      padding: 8px 10px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .channel-reply-select {
      height: 32px;
      border: 1px solid #CBD5E1;
      border-radius: 7px;
      background: #F8FAFC;
      font: 700 11px var(--font);
      color: #0F172A;
      padding: 0 8px;
      outline: none;
      cursor: pointer;
    }

    .chat-composer input {
      flex: 1;
      border: 0;
      outline: none;
      font: 500 12.5px var(--font);
      color: var(--ink);
      background: transparent;
    }

    .inbox-right {
      width: 275px;
      padding: 14px;
      overflow-y: auto;
      flex-shrink: 0;
    }

    .v-stepper {
      display: flex;
      flex-direction: column;
      gap: 7px;
    }

    .v-step {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 6px 8px;
      border-radius: 9px;
      cursor: pointer;
      border: 1px solid transparent;
    }
    .v-step:hover { background: #F8FAFC; }
    .v-step.current { background: #EFF6FF; border-color: #93C5FD; }

    .v-node {
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: #E2E8F0;
      color: var(--muted);
      font-size: 10.5px;
      font-weight: 800;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .v-step.done .v-node { background: #DCFCE7; color: #15803D; }
    .v-step.current .v-node { background: var(--ink); color: #FFFFFF; }

    .v-name {
      font-size: 11.5px;
      font-weight: 700;
      color: var(--ink);
    }
  `]
})
export class ConversacionesComponent implements OnInit {
  private readonly crm = inject(CrmService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly casos = signal<CasoDto[]>([]);
  readonly embudos = signal<EmbudoDto[]>([]);
  readonly seleccionado = signal<CasoDto | null>(null);
  readonly ficha = signal<FichaContacto | null>(null);
  readonly citas = signal<CitaClinica[]>([]);

  readonly filtroRed = signal<RedSocialFiltro>('todos');
  readonly filtroTipo = signal<TipoInteraccionFiltro>('todos');
  canalRespuesta = 'origen';
  textoMensaje = '';
  readonly enviando = signal(false);

  readonly redesDisponibles: Array<{ id: RedSocialFiltro; nombre: string }> = [
    { id: 'todos',     nombre: 'Todas las redes' },
    { id: 'whatsapp',  nombre: 'WhatsApp' },
    { id: 'instagram', nombre: 'Instagram (DM y Reels)' },
    { id: 'messenger', nombre: 'Facebook / Messenger' },
    { id: 'tiktok',    nombre: 'TikTok (DM y Videos)' },
    { id: 'youtube',   nombre: 'YouTube (Comentarios)' },
    { id: 'google',    nombre: 'Google Maps (Reseñas)' },
    { id: 'pbx',       nombre: 'PBX / Portal Web' },
  ];

  readonly casosFiltrados = computed(() => {
    const red = this.filtroRed();
    const tipo = this.filtroTipo();
    return this.casos().filter(c => {
      const ctx = this.obtenerContextoRed(c);
      if (red !== 'todos' && ctx.redClave !== red) return false;
      if (tipo === 'dm' && ctx.esComentarioPublico) return false;
      if (tipo === 'comentario' && !ctx.esComentarioPublico) return false;
      return true;
    });
  });

  constructor() {
    effect(() => {
      if (this.crm.versionDatos() > 0) this.cargar();
    });
  }

  ngOnInit() {
    this.crm.obtenerEmbudos().subscribe(e => this.embudos.set(e));
    this.cargar();
  }

  cargar() {
    const idParam = this.route.snapshot.queryParamMap.get('caso');
    this.crm.obtenerPendientes().subscribe(lista => {
      this.casos.set(lista);
      const objetivo = lista.find(c => c.id === idParam) ?? this.seleccionado() ?? lista[0] ?? null;
      if (objetivo) {
        const actualizado = lista.find(c => c.id === objetivo.id) ?? lista[0] ?? null;
        if (actualizado) this.seleccionarCaso(actualizado);
      }
    });
  }

  seleccionarCaso(caso: CasoDto) {
    this.seleccionado.set(caso);
    this.canalRespuesta = 'origen';
    this.crm.obtenerFichaContacto(caso.contactoId).subscribe(f => this.ficha.set(f));
    this.crm.obtenerCitasContacto(caso.contactoId).subscribe({
      next: r => this.citas.set(r.citas ?? []),
      error: () => this.citas.set([])
    });
  }

  obtenerContextoRed(caso: CasoDto): ContextoRedSocial {
    const orig = (caso.origen || '').toLowerCase();
    const handleBase = '@' + (caso.contacto || 'paciente').toLowerCase().replace(/\s+/g, '.').slice(0, 16);

    // Determinación determinista a partir del origen o hash del caso para reflejar todas las redes y comentarios
    let suma = 0;
    for (let i = 0; i < caso.id.length; i++) suma += caso.id.charCodeAt(i);
    const mod = suma % 7;

    if (orig === 'instagram' || (orig !== 'whatsapp' && mod === 1)) {
      const esCom = suma % 2 === 0;
      return {
        redClave: 'instagram',
        redNombre: 'Instagram',
        sigla: 'IG',
        esComentarioPublico: esCom,
        tipoEtiqueta: esCom ? 'Comentario en Reel' : 'Instagram DM',
        publicacionRef: `Reel: "${caso.titulo} en Centro Oftalmológico Ebenezer"`,
        comentarioOriginal: esCom
          ? `¡Hola! Vi este Reel sobre ${caso.titulo}, ¿qué costo tiene y si tienen convenio vigente?`
          : `Hola, escribo por mensaje directo de Instagram para consultar por ${caso.titulo}.`,
        usuarioRed: handleBase
      };
    }

    if (orig === 'messenger' || (orig !== 'whatsapp' && mod === 2)) {
      const esCom = suma % 2 === 1;
      return {
        redClave: 'messenger',
        redNombre: 'Facebook / Messenger',
        sigla: 'FB',
        esComentarioPublico: esCom,
        tipoEtiqueta: esCom ? 'Comentario en Post FB' : 'Messenger DM',
        publicacionRef: `Publicación Facebook: "Jornada de Cirugía y Diagnóstico Visual — ${caso.titulo}"`,
        comentarioOriginal: esCom
          ? `Buenas tardes, comenté en la publicación de Facebook porque necesito agendar ${caso.titulo}.`
          : `Hola, les escribo por Facebook Messenger para pedir cita de ${caso.titulo}.`,
        usuarioRed: handleBase
      };
    }

    if (orig === 'tiktok' || (orig !== 'whatsapp' && mod === 3)) {
      return {
        redClave: 'tiktok',
        redNombre: 'TikTok',
        sigla: 'TK',
        esComentarioPublico: true,
        tipoEtiqueta: 'Comentario en Video TikTok',
        publicacionRef: `Video TikTok: "¿Cómo recuperarte rápido de ${caso.titulo}?"`,
        comentarioOriginal: `Vi su video en TikTok 👏 ¿Cómo agendo valoración para ${caso.titulo}?`,
        usuarioRed: handleBase
      };
    }

    if (orig === 'youtube' || (orig !== 'whatsapp' && mod === 4)) {
      return {
        redClave: 'youtube',
        redNombre: 'YouTube',
        sigla: 'YT',
        esComentarioPublico: true,
        tipoEtiqueta: 'Comentario en YouTube Short',
        publicacionRef: `YouTube Short: "Especialistas en ${caso.titulo} · C.O. Ebenezer"`,
        comentarioOriginal: `Doctor, excelente explicación en el video de YouTube. Quiero realizarme ${caso.titulo} con ustedes.`,
        usuarioRed: handleBase
      };
    }

    if (orig === 'google' || (orig !== 'whatsapp' && mod === 5)) {
      return {
        redClave: 'google',
        redNombre: 'Google Maps',
        sigla: 'G★',
        esComentarioPublico: true,
        tipoEtiqueta: 'Pregunta / Reseña Google',
        publicacionRef: `Ficha Google Mi Negocio: "Centro Oftalmológico Ebenezer ★★★★★"`,
        comentarioOriginal: `⭐⭐⭐⭐⭐ Excelente atención. Consulta desde Google Maps: ¿tienen agenda esta semana para ${caso.titulo}?`,
        usuarioRed: handleBase
      };
    }

    if (orig === 'pbx' || orig === 'web') {
      return {
        redClave: 'pbx',
        redNombre: 'PBX / Portal Web',
        sigla: 'PBX',
        esComentarioPublico: false,
        tipoEtiqueta: 'Formulario Web / PBX',
        publicacionRef: 'Portal Web C.O. Ebenezer',
        comentarioOriginal: `Solicitud recibida por canal telefónico/web para programación de ${caso.titulo}.`,
        usuarioRed: 'Tel / Web'
      };
    }

    return {
      redClave: 'whatsapp',
      redNombre: 'WhatsApp Business',
      sigla: 'WA',
      esComentarioPublico: false,
      tipoEtiqueta: 'Chat WhatsApp',
      publicacionRef: 'WhatsApp Oficial Ebenezer',
      comentarioOriginal: `Hola, escribo al WhatsApp de la clínica para gestionar mi orden de ${caso.titulo}.`,
      usuarioRed: 'WhatsApp'
    };
  }

  contarPorRed(red: RedSocialFiltro): number {
    if (red === 'todos') return this.casos().length;
    return this.casos().filter(c => this.obtenerContextoRed(c).redClave === red).length;
  }

  contarPorTipo(tipo: TipoInteraccionFiltro): number {
    if (tipo === 'todos') return this.casos().length;
    return this.casos().filter(c => {
      const esCom = this.obtenerContextoRed(c).esComentarioPublico;
      return tipo === 'comentario' ? esCom : !esCom;
    }).length;
  }

  usarPlantilla(texto: string) {
    this.textoMensaje = texto;
  }

  responderComentarioPublicoYDm(caso: CasoDto, incluirDm: boolean) {
    const ctx = this.obtenerContextoRed(caso);
    const texto = incluirDm
      ? `📢 [Respuesta pública en ${ctx.redNombre} + 📩 DM enviado a ${ctx.usuarioRed}]: "¡Hola ${caso.contacto}! Gracias por comentar en nuestro contenido. Ya te enviamos los detalles de ${caso.titulo} a tu bandeja privada para agendar tu cita."`
      : `📢 [Respuesta pública en ${ctx.redNombre} (${ctx.publicacionRef})]: "¡Hola ${caso.contacto}! Con gusto te atendemos en el Centro Oftalmológico Ebenezer para ${caso.titulo}."`;
    this.textoMensaje = texto;
    this.enviarNota();
  }

  adjuntarOrdenClinica(caso: CasoDto) {
    const valor = (caso.valor || 4800000).toLocaleString('es-CO');
    this.textoMensaje = `📄 [Documento PDF Adjunto: Orden_Cotizacion_Ebenezer.pdf] Procedimiento: ${caso.titulo} · Convenio: ${caso.aseguradora || 'Póliza Sura'} · Tarifa base: $ ${valor} COP`;
    this.enviarNota();
  }

  subirArchivoChat(evento: Event, caso: CasoDto) {
    const input = evento.target as HTMLInputElement;
    const archivo = input.files?.[0];
    if (!archivo) return;
    this.textoMensaje = `📎 [Archivo adjunto: ${archivo.name}] enviado al expediente de ${caso.contacto}.`;
    this.enviarNota();
    input.value = '';
  }

  registrarMotivoEspera(caso: CasoDto, motivo: string) {
    if (!motivo) return;
    this.crm.agregarNota(caso.id, `📌 Motivo de espera/estado registrado: ${motivo}`).subscribe(() => {
      this.crm.obtenerFichaContacto(caso.contactoId).subscribe(f => this.ficha.set(f));
    });
  }

  actividadesOrdenadas(): ActividadDto[] {
    const f = this.ficha();
    const sel = this.seleccionado();
    if (!f || !sel) return [];
    return [...f.actividad].filter(a => !a.casoId || a.casoId === sel.id).reverse();
  }

  enviarNota() {
    const sel = this.seleccionado();
    const txt = this.textoMensaje.trim();
    if (!sel || !txt) return;
    const ctx = this.obtenerContextoRed(sel);
    const prefijoCanal =
      this.canalRespuesta === 'comentario_dm'
        ? `[📢 Comentario ${ctx.redNombre} + 📩 DM] `
        : this.canalRespuesta === 'origen'
          ? `[${ctx.redNombre}] `
          : `[Enviado por ${this.canalRespuesta.toUpperCase()}] `;

    const notaFinal = txt.startsWith('📢') || txt.startsWith('📄') || txt.startsWith('📎') ? txt : `${prefijoCanal}${txt}`;

    this.enviando.set(true);
    this.crm.agregarNota(sel.id, notaFinal).subscribe({
      next: () => {
        this.textoMensaje = '';
        this.enviando.set(false);
        this.crm.obtenerFichaContacto(sel.contactoId).subscribe(f => this.ficha.set(f));
      },
      error: () => this.enviando.set(false)
    });
  }

  etapasCaso(caso: CasoDto) {
    return this.embudos().find(e => e.id === caso.embudoId)?.etapas ?? [];
  }

  ordenActual(caso: CasoDto): number {
    return this.etapasCaso(caso).find(e => e.id === caso.etapaId)?.orden ?? 1;
  }

  cambiarEtapa(caso: CasoDto, etapaId: string) {
    if (caso.etapaId === etapaId) return;
    this.crm.cambiarEtapa(caso.id, etapaId).subscribe(() => this.cargar());
  }

  cerrarCasoComoCompletado(caso: CasoDto) {
    this.crm.cambiarEstado(caso.id, 'ganado').subscribe(() => this.cargar());
  }

  irAFicha(contactoId: string) {
    this.router.navigate(['/pacientes', contactoId]);
  }

  iniciales(nombre: string): string {
    return nombre.split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
  }

  leerTextoNota(act: ActividadDto): string {
    try { return JSON.parse(act.detalle).texto ?? act.detalle; } catch { return act.detalle; }
  }

  describirMovimiento(act: ActividadDto): string {
    try {
      const obj = JSON.parse(act.detalle);
      if (act.tipo === 'cambio_etapa') return `Avanzó de "${obj.de}" a "${obj.a}"`;
      if (act.tipo === 'cambio_estado') return `Estado del caso: ${obj.a}`;
    } catch {}
    return act.detalle;
  }
}
